/**
 * AddScoreModal Component
 *
 * Modal dialog for managing investment scores:
 * - Step 1: Select a score category (shows checkmark if category has selections)
 * - Step 2: Select score(s) based on category selection_type (S=single, M=multiple)
 * - Supports removing existing scores from investment
 */

import { Button, Spinner } from '@medsuite/ui-components';
import { useState, useEffect } from 'react';

import type { CategoryWithScores, InvestmentScoreGrouped } from '@medsuite/api-client';

interface AddScoreModalProps {
  isOpen: boolean;
  onClose: () => void;
  categories: CategoryWithScores[];
  selectedScoreIds: number[];
  investmentScoreCategories?: InvestmentScoreGrouped[];
  onAddScore: (scoreId: number) => Promise<void>;
  onReplaceScore: (categoryId: number, scoreId: number) => Promise<void>;
  onRemoveScore?: (investmentScoreId: number) => Promise<void>;
  isLoading?: boolean;
}

export default function AddScoreModal({
  isOpen,
  onClose,
  categories,
  selectedScoreIds,
  investmentScoreCategories = [],
  onAddScore,
  onReplaceScore,
  onRemoveScore,
  isLoading = false,
}: AddScoreModalProps) {
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [selectedScores, setSelectedScores] = useState<number[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isRemoving, setIsRemoving] = useState<number | null>(null);

  // Get selected category
  const selectedCategory = categories.find((c) => c.score_category_id === selectedCategoryId);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedCategoryId(null);
      setSelectedScores([]);
    }
  }, [isOpen]);

  // Get investment scores for a category
  const getInvestmentScoresForCategory = (categoryId: number) => {
    const category = investmentScoreCategories.find((c) => c.score_category_id === categoryId);
    return category?.investment_scores || [];
  };

  // Handle category selection
  const handleCategorySelect = (categoryId: number) => {
    setSelectedCategoryId(categoryId);
    setSelectedScores([]);
  };

  // Handle score toggle for multi-select
  const handleScoreToggle = (scoreId: number) => {
    if (!selectedCategory) {
      return;
    }

    if (selectedCategory.selection_type === 'S') {
      // Single selection - just set the score
      setSelectedScores([scoreId]);
    } else {
      // Multiple selection - toggle
      setSelectedScores((prev) =>
        prev.includes(scoreId) ? prev.filter((id) => id !== scoreId) : [...prev, scoreId]
      );
    }
  };

  // Handle removing an investment score
  const handleRemoveScore = async (investmentScoreId: number) => {
    if (!onRemoveScore) {
      return;
    }

    setIsRemoving(investmentScoreId);
    try {
      await onRemoveScore(investmentScoreId);
    } catch (error) {
      console.error('Failed to remove score:', error);
    } finally {
      setIsRemoving(null);
    }
  };

  // Handle save
  const handleSave = async () => {
    if (!selectedCategory || selectedScores.length === 0) {
      return;
    }

    setIsSaving(true);
    try {
      if (selectedCategory.selection_type === 'S') {
        // Single selection - replace
        await onReplaceScore(selectedCategory.score_category_id, selectedScores[0]);
      } else {
        // Multiple selection - add scores one by one (to handle already selected)
        for (const scoreId of selectedScores) {
          if (!selectedScoreIds.includes(scoreId)) {
            await onAddScore(scoreId);
          }
        }
      }
      onClose();
    } catch (error) {
      console.error('Failed to save scores:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Go back to category selection
  const handleBack = () => {
    setSelectedCategoryId(null);
    setSelectedScores([]);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-xl shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              {selectedCategoryId && (
                <button
                  onClick={handleBack}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
              )}
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {selectedCategoryId ? selectedCategory?.score_category_name : 'Manage Scores'}
              </h2>
              {selectedCategory && (
                <span
                  className={`text-xs px-2 py-0.5 rounded ${
                    selectedCategory.selection_type === 'S'
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
                      : 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300'
                  }`}
                >
                  {selectedCategory.selection_type === 'S' ? 'Select One' : 'Select Multiple'}
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="p-6 max-h-[60vh] overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner />
              </div>
            ) : !selectedCategoryId ? (
              /* Step 1: Category Selection */
              <div className="space-y-2">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Select a score category to add or manage scores:
                </p>
                {categories.map((category) => {
                  const selectedInCategory = category.scores.filter((s) =>
                    selectedScoreIds.includes(s.score_id)
                  ).length;
                  const hasSelections = selectedInCategory > 0;

                  return (
                    <button
                      key={category.score_category_id}
                      onClick={() => handleCategorySelect(category.score_category_id)}
                      className="w-full p-4 text-left rounded-lg border border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {/* Checkmark icon if category has selections */}
                          {hasSelections && (
                            <svg
                              className="w-5 h-5 text-green-500"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            {category.score_category_name}
                          </span>
                          <span
                            className={`text-xs px-2 py-0.5 rounded ${
                              category.selection_type === 'S'
                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
                                : 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300'
                            }`}
                          >
                            {category.selection_type === 'S' ? 'Single' : 'Multiple'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {selectedInCategory} / {category.scores.length}
                          </span>
                          <svg
                            className="w-5 h-5 text-gray-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              /* Step 2: Score Selection */
              <div className="space-y-4">
                {/* Currently selected scores in this category (for removal) */}
                {(() => {
                  const existingScores = getInvestmentScoresForCategory(selectedCategoryId);
                  if (existingScores.length > 0) {
                    return (
                      <div className="mb-4">
                        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Currently Selected
                        </h3>
                        <div className="space-y-2">
                          {existingScores.map((invScore) => (
                            <div
                              key={invScore.investment_score_id}
                              className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800"
                            >
                              <div className="flex items-center gap-2">
                                <svg
                                  className="w-4 h-4 text-green-500"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                <span className="font-medium text-gray-900 dark:text-gray-100">
                                  {invScore.score_name}
                                </span>
                                <span className="text-sm text-indigo-600 dark:text-indigo-400">
                                  {invScore.points} pts
                                </span>
                              </div>
                              {onRemoveScore && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveScore(invScore.investment_score_id);
                                  }}
                                  disabled={isRemoving === invScore.investment_score_id}
                                  className="p-1.5 text-red-600 hover:text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                                  title="Remove from investment"
                                >
                                  {isRemoving === invScore.investment_score_id ? (
                                    <Spinner />
                                  ) : (
                                    <svg
                                      className="w-4 h-4"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                      />
                                    </svg>
                                  )}
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                        <hr className="my-4 border-gray-200 dark:border-gray-700" />
                      </div>
                    );
                  }
                  return null;
                })()}

                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedCategory?.selection_type === 'S'
                    ? 'Select one score (will replace any existing selection in this category):'
                    : 'Select one or more scores to add:'}
                </p>
                <div className="space-y-2">
                  {selectedCategory?.scores.map((score) => {
                    const isAlreadySelected = selectedScoreIds.includes(score.score_id);
                    const isCurrentlySelected = selectedScores.includes(score.score_id);
                    const isDisabled = selectedCategory.selection_type === 'M' && isAlreadySelected;

                    return (
                      <button
                        key={score.score_id}
                        onClick={() => !isDisabled && handleScoreToggle(score.score_id)}
                        disabled={isDisabled}
                        className={`w-full p-4 text-left rounded-lg border transition-colors ${
                          isCurrentlySelected
                            ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30'
                            : isAlreadySelected
                              ? 'border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-700/50 opacity-60'
                              : 'border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-600'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              {/* Checkbox/Radio indicator */}
                              <div
                                className={`w-5 h-5 rounded-${
                                  selectedCategory.selection_type === 'S' ? 'full' : 'md'
                                } border-2 flex items-center justify-center ${
                                  isCurrentlySelected
                                    ? 'border-indigo-500 bg-indigo-500'
                                    : 'border-gray-300 dark:border-gray-600'
                                }`}
                              >
                                {isCurrentlySelected && (
                                  <svg
                                    className="w-3 h-3 text-white"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                )}
                              </div>
                              <span className="font-medium text-gray-900 dark:text-gray-100">
                                {score.score_name}
                              </span>
                              {isAlreadySelected && (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300">
                                  Already Added
                                </span>
                              )}
                            </div>
                            <div className="mt-2 flex items-center gap-4 ml-7">
                              <span className="inline-flex items-center px-2 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 text-sm font-semibold">
                                {score.points} pts
                              </span>
                              <div className="grid grid-cols-5 gap-3 text-xs">
                                <div>
                                  <span className="text-gray-500 dark:text-gray-400">Sec:</span>{' '}
                                  <span className="font-medium">{score.security_score}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500 dark:text-gray-400">Grw:</span>{' '}
                                  <span className="font-medium">{score.growth_score}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500 dark:text-gray-400">Eff:</span>{' '}
                                  <span className="font-medium">{score.efficiency_score}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500 dark:text-gray-400">Inf:</span>{' '}
                                  <span className="font-medium">{score.infrastructure_score}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500 dark:text-gray-400">Cln:</span>{' '}
                                  <span className="font-medium">{score.clinical_score}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl">
            <Button variant="secondary" onClick={onClose} disabled={isSaving}>
              {selectedCategoryId ? 'Done' : 'Close'}
            </Button>
            {selectedCategoryId && selectedScores.length > 0 && (
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={selectedScores.length === 0 || isSaving}
              >
                {isSaving
                  ? 'Adding...'
                  : `Add ${selectedScores.length} Score${selectedScores.length !== 1 ? 's' : ''}`}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
